import { SUCCESS_OUTCOMES, type ConversationPaths } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MessagesSquare } from "lucide-react";
import { z } from "zod";
import { SuccessRate } from "@/components/domain/analytics";
import { PageHeader } from "@/components/domain/misc";
import { Page, QueryState } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Select } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/misc";
import { MiniStat } from "@/features/analytics/bits";
import { PATHS, sessionSearch } from "@/features/analytics/links";
import { PathFlow } from "@/features/paths/path-flow";
import { GoldenFailingPaths, PathFindings } from "@/features/paths/path-lists";
import { UseCaseOptions } from "@/features/sessions/session-filters";
import { q } from "@/lib/api";
import { fmtInt } from "@/lib/format";

const searchSchema = z.object({
  agentId: z.string().min(1).optional().catch(undefined),
  useCaseId: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/paths")({
  validateSearch: searchSchema,
  component: PathsPage,
});

function PathsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const agents = useQuery(q.agents());
  const useCases = useQuery(q.useCases(search.agentId ? { agentId: search.agentId } : {}));
  const scope = { agentId: search.agentId, useCaseId: search.useCaseId };
  const paths = useQuery(q.paths(scope));
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  return (
    <Page wide>
      <PageHeader
        title="Conversation paths"
        description="How conversations move through each agent's discovered stages and where they end. Compare the paths that succeed with the ones that fail, and turn what differs into signals."
        actions={
          <>
            <Select
              aria-label="Agent"
              value={search.agentId ?? ""}
              onChange={(e) => {
                const agentId = e.target.value || undefined;
                // A use case belongs to one agent; drop it when it no longer fits the agent.
                const keep = search.useCaseId && useCases.data?.find((u) => u.id === search.useCaseId)?.agentIds.includes(agentId ?? "");
                setSearch({ agentId, useCaseId: keep ? search.useCaseId : undefined });
              }}
              className="w-44"
            >
              <option value="">All agents</option>
              {agents.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
            <Select aria-label="Use case" value={search.useCaseId ?? ""} onChange={(e) => setSearch({ useCaseId: e.target.value || undefined })} className="w-56">
              <option value="">All use cases</option>
              {search.useCaseId && useCases.data && !useCases.data.some((u) => u.id === search.useCaseId) ? <option value={search.useCaseId}>{search.useCaseId}</option> : null}
              {useCases.data ? <UseCaseOptions useCases={useCases.data} /> : null}
            </Select>
          </>
        }
      />
      <QueryState
        query={paths}
        loading={
          <div className="flex flex-col gap-4">
            <Skeleton className="h-[460px]" />
            <div className="grid gap-4 lg:grid-cols-2">
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
            </div>
          </div>
        }
      >
        {(p) => <PathsBody paths={p} scope={scope} />}
      </QueryState>
    </Page>
  );
}

function summarize(p: ConversationPaths) {
  const byId = new Map(p.nodes.map((n) => [n.id, n]));
  const entering = p.links.filter((l) => byId.get(l.source)?.depth === 0).reduce((s, l) => s + l.sessions, 0);
  const ended = p.links.filter((l) => byId.get(l.target)?.terminal);
  const endedTotal = ended.reduce((s, l) => s + l.sessions, 0);
  const success = ended.filter((l) => SUCCESS_OUTCOMES.includes(byId.get(l.target)!.terminal!)).reduce((s, l) => s + l.sessions, 0);
  const stages = new Set(p.nodes.filter((n) => !n.terminal).map((n) => n.label)).size;
  return { entering, success: endedTotal ? success / endedTotal : 0, stages, steps: Math.max(0, ...p.nodes.filter((n) => !n.terminal).map((n) => n.depth + 1)) };
}

function PathsBody({ paths, scope }: { paths: ConversationPaths; scope: { agentId?: string; useCaseId?: string } }) {
  const s = summarize(paths);
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="Flow"
          description="Columns are the order of stages within a session; the last column is the outcome. Band width is volume, color is the success rate of sessions taking that transition."
          actions={
            <Button variant="ghost" size="sm" asChild>
              <Link to={PATHS.sessions} search={sessionSearch(scope)}>
                <MessagesSquare /> Sessions
              </Link>
            </Button>
          }
        />
        <div className="flex flex-wrap gap-x-8 gap-y-2 border-b px-4 py-3">
          <MiniStat label="Staged sessions" value={fmtInt(s.entering)} />
          <MiniStat label="End in success" value={<SuccessRate value={s.success} />} />
          <MiniStat label="Distinct stages" value={s.stages} />
          <MiniStat label="Longest path" value={`${s.steps} steps`} />
        </div>
        <CardBody>
          <PathFlow paths={paths} />
        </CardBody>
      </Card>
      <GoldenFailingPaths paths={paths} />
      <PathFindings paths={paths} scope={scope} />
    </div>
  );
}
